/**
 * Generates column data for a single property error row.
 * Each field becomes an EditableCell with error state and type-specific input.
 */

import { TableCellType } from '@/types/property.types';
import {
  bathRoomOptions,
  bedRoomOptions,
  booleanOptions,
  orderStateOptions,
  propertyTypeOptions,
} from '@/config/options.config';
import { getEntityIdOptions } from '@/services/entity-tree';
import EditableCell, { type CellChangeData } from './EditableCell';
import type { ErrorCell } from '../../fix-errors/utils/fixErrorFieldMaps';

// ---- Types ----------------------------------------------------------------

interface PropertyErrorData {
  id: number;
  propertyType: ErrorCell;
  unitNumber: ErrorCell;
  address: ErrorCell;
  city: ErrorCell;
  state: ErrorCell;
  zipCode: ErrorCell;
  propertyId: ErrorCell;
  bedrooms: ErrorCell;
  bathrooms: ErrorCell;
  squareFeet: ErrorCell;
  unitId: ErrorCell;
  portfolioName: ErrorCell;
  propertyStatus: ErrorCell;
  rentReady: ErrorCell;
  listingDate: ErrorCell;
  isListed: ErrorCell;
  daysOnMarket: ErrorCell;
  availableDate: ErrorCell;
  leaseSignedDate: ErrorCell;
  leaseStartDate: ErrorCell;
  leaseEndDate: ErrorCell;
  moveInDate: ErrorCell;
  noticeGivenDate: ErrorCell;
  moveOutDate: ErrorCell;
  assignee: ErrorCell;
  isActive: ErrorCell;
  entity: ErrorCell;
  propId: ErrorCell;
}

interface CellDef {
  key: string;
  type: TableCellType;
  options?: Array<{ value: string; label: string }>;
  isOptionalField?: boolean;
  isDecimal?: boolean;
  maxCount?: number;
}

// ---- Field config ---------------------------------------------------------

const PROPERTY_CELL_DEFS: CellDef[] = [
  { key: 'propertyType', type: TableCellType.SELECT, options: propertyTypeOptions },
  { key: 'unitNumber', type: TableCellType.TEXT, isOptionalField: true },
  { key: 'address', type: TableCellType.TEXT },
  { key: 'city', type: TableCellType.TEXT },
  { key: 'state', type: TableCellType.SELECT, options: orderStateOptions },
  { key: 'zipCode', type: TableCellType.ZIP },
  { key: 'propertyId', type: TableCellType.TEXT, isOptionalField: true, maxCount: 50 },
  { key: 'bedrooms', type: TableCellType.SELECT, options: bedRoomOptions, isOptionalField: true },
  { key: 'bathrooms', type: TableCellType.SELECT, options: bathRoomOptions, isOptionalField: true },
  { key: 'squareFeet', type: TableCellType.NUMBER, isOptionalField: true, isDecimal: true },
  { key: 'unitId', type: TableCellType.TEXT, isOptionalField: true },
  { key: 'portfolioName', type: TableCellType.TEXT, isOptionalField: true },
  { key: 'propertyStatus', type: TableCellType.TEXT, isOptionalField: true, maxCount: 50 },
  { key: 'rentReady', type: TableCellType.SELECT, options: booleanOptions, isOptionalField: true },
  { key: 'listingDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'isListed', type: TableCellType.SELECT, options: booleanOptions, isOptionalField: true },
  { key: 'daysOnMarket', type: TableCellType.NUMBER, isOptionalField: true },
  { key: 'availableDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'leaseSignedDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'leaseStartDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'leaseEndDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'moveInDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'noticeGivenDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'moveOutDate', type: TableCellType.DATE, isOptionalField: true },
  { key: 'assignee', type: TableCellType.TEXT, isOptionalField: true },
  { key: 'isActive', type: TableCellType.SELECT, options: booleanOptions, isOptionalField: true },
  { key: 'entity', type: TableCellType.SELECT, options: getEntityIdOptions() },
];

// ---- Generator ------------------------------------------------------------

export const generatePropertyRow = (
  data: PropertyErrorData,
  setData: (change: CellChangeData) => void
): Record<string, { component: React.ReactNode; data?: string | null }> => {
  const { id } = data;
  const row: Record<string, { component: React.ReactNode; data?: string | null }> = { id: { component: null, data: String(id) } };

  for (const def of PROPERTY_CELL_DEFS) {
    const cell = data[def.key as keyof PropertyErrorData] as ErrorCell;
    row[def.key] = {
      component: (
        <EditableCell
          isActive={cell.isActive}
          value={cell.value}
          errorMessage={cell.errorMessage}
          type={def.type}
          options={def.options}
          id={id}
          name={def.key}
          setData={setData}
          isOptionalField={def.isOptionalField}
          isDecimal={def.isDecimal}
          maxCount={def.maxCount}
        />
      ),
      data: cell.value,
    };
  }

  // propId is the hidden server-side ID column
  const propIdCell = data.propId;
  row.propId = {
    component: (
      <EditableCell
        isActive={propIdCell.isActive}
        value={propIdCell.value}
        errorMessage={propIdCell.errorMessage}
        type={TableCellType.TEXT}
        id={id}
        name="propId"
        setData={setData}
      />
    ),
    data: propIdCell.value,
  };

  return row;
};
